import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cartCurrencySelector, cartTotal } from "../redux/selectors/cartSelector";
import { getPromoFromArray } from "../utils";
import { promoCodesListSelector } from "../redux/selectors/pagesSelectors";

/**
 * Rounds a numeric value **up** to the nearest multiple of `step` (ceiling to step).
 *
 * Examples:
 *  - ceilToStep(9.99, 1)      => 10
 *  - ceilToStep(5.17, 0.05)   => 5.20
 *  - ceilToStep(5.170000000000002, 0.05) => 5.20
 *  - ceilToStep(5.17, 0.5)    => 5.5
 *
 * @param {number|string} value
 * @param {number|string} [step=0.05]
 * @returns {number}
 */
const ceilToStep = (value, step = 0.05) => {
    const v = Number(value);
    const s = Number(step);

    if (!Number.isFinite(v) || !Number.isFinite(s) || s <= 0) return 0;

    // сколько знаков после запятой нужно, чтобы представить step как int
    const decimals = (s.toString().split('.')[1] || '').length;
    const scale = 10 ** decimals;

    const vInt = Math.round(v * scale);
    const sInt = Math.round(s * scale);

    return (Math.ceil(vInt / sInt) * sInt) / scale;
};

function useCartTotals({ productId = null, promo = null } = {}) {
    const currency = useSelector(cartCurrencySelector);
    const promoCodesList = useSelector(promoCodesListSelector);
    const promoResolved = promo ?? getPromoFromArray(promoCodesList);

    const selectTotal = useMemo(() => cartTotal(null), []);
    const total = useSelector(selectTotal);
    const discountUnit = total?.discount_unit ?? 'percent';
    const discountPercent = total?.discount ?? 0;
    const signHtml = currency?.sign ?? '';

    const baseTotal = total?.total ?? 0;
    const subtotal = total?.subtotal ?? 0;

    const discountAmount = useMemo(() => {
        if (subtotal > 0 && baseTotal > 0) return subtotal - baseTotal;
        return 0;
    }, [subtotal, baseTotal]);

    const promoProductId = promoResolved?.product_id ?? null;
    const promoDiscount = Number(promoResolved?.discount) || 0;

    const totalWithPromo = useMemo(() => {
        let sum = baseTotal;
        if (promoProductId != null && productId != null && +promoProductId === +productId) {
            sum = (baseTotal * promoDiscount) / 100; // твоя исходная формула
        }
        return sum;
    }, [baseTotal, promoProductId, promoDiscount, productId]);

    return useMemo(() => ({
        signHtml,
        totalWithPromo,
        discountAmount:  Number(ceilToStep(discountAmount, 0.02).toFixed(2)),
        discountUnit,
        discountPercent:  Number(ceilToStep(discountPercent, 0.02).toFixed(2)),
        hasTotal: baseTotal > 0,
        hasDiscount: discountAmount > 0 && baseTotal > 0,
    }), [signHtml, totalWithPromo, discountAmount, discountUnit, discountPercent, baseTotal]);
}

export default useCartTotals;